import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { PracticeService } from '../services/database';
import { sortPractices } from '../utils/practiceUtils';
import type { Practice, Team } from '../types';

interface UsePracticesProps {
  currentTeam: Team | null;
}

export function usePractices({ currentTeam }: UsePracticesProps) {
  const [practices, setPractices] = useState<Practice[]>([]);
  const [selectedPractice, setSelectedPractice] = useState<Practice | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingPractice, setIsAddingPractice] = useState(false);
  const [editingPractice, setEditingPractice] = useState<Practice | null>(null);
  
  // Track the last team ID to detect team changes
  const lastTeamIdRef = useRef<string | null>(null);
  
  // Fetch practices for the current team - define this first since it's used in effects below
  const fetchPractices = useCallback(async () => {
    if (!currentTeam?.id) {
      console.log(`[usePractices] No team selected, clearing practices`);
      setPractices([]);
      return;
    }

    console.log(`[usePractices] Fetching practices for team: ${currentTeam.name} (${currentTeam.id})`);
    setIsLoading(true);

    try {
      // Force fresh data by disabling cache and adding timestamp
      const { data, error } = await supabase
        .from('practices')
        .select('*')
        .eq('team_id', currentTeam.id)
        .order('date', { ascending: true }); // Get all practices, we'll sort them ourselves

      if (error) throw error;

      const practiceList = data || [];
      console.log(`[usePractices] Fetched ${practiceList.length} practices from database for team ${currentTeam.id}`);
      
      // Sort practices with smart ordering (future first, then past)
      const sortedPractices = sortPractices(practiceList);
      
      // Always update practices, even if empty array
      setPractices(sortedPractices);

    } catch (error) {
      console.error('[usePractices] Error fetching practices:', error);
      setPractices([]);
      toast.error('Failed to load practices');
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam]);

  // Clear state when team changes
  useEffect(() => {
    if (currentTeam?.id !== lastTeamIdRef.current) {
      console.log(`[usePractices] Team changed from ${lastTeamIdRef.current} to ${currentTeam?.id}`);
      setPractices([]);
      setSelectedPractice(null);
      lastTeamIdRef.current = currentTeam?.id || null;
    }
  }, [currentTeam?.id]);
  
  // Listen for cache clear and team change events to immediately clear practices
  useEffect(() => {
    const handleCacheClear = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { teamId, newTeamId } = customEvent.detail || {};
      
      console.log(`[usePractices] Cache clear event received for team: ${teamId || newTeamId}`);
      
      // Clear practices and selected practice immediately
      setPractices([]);
      setSelectedPractice(null);
    };
    
    const handleTeamChanged = (event: Event) => {
      const customEvent = event as CustomEvent;
      const newTeamId = customEvent.detail?.teamId;
      
      console.log(`[usePractices] Team changed event received for team: ${newTeamId}`);
      
      // Clear practices and selected practice immediately, then refetch after a delay
      setPractices([]);
      setSelectedPractice(null);
      
      // If there's a new team, refetch practices after a short delay
      if (newTeamId && currentTeam?.id === newTeamId) {
        setTimeout(() => {
          console.log(`[usePractices] Refetching practices for team: ${newTeamId}`);
          fetchPractices();
        }, 150);
      }
    };
    
    window.addEventListener('clear-team-cache', handleCacheClear);
    window.addEventListener('team-changed', handleTeamChanged);
    
    return () => {
      window.removeEventListener('clear-team-cache', handleCacheClear);
      window.removeEventListener('team-changed', handleTeamChanged);
    };
  }, [currentTeam, fetchPractices]);
  
  // Load practices when team changes
  useEffect(() => {
    console.log(`[usePractices] Team effect triggered, currentTeam: ${currentTeam?.id}`);
    if (currentTeam?.id) {
      // Add a small delay to ensure team change is fully processed
      const timeoutId = setTimeout(() => {
        console.log(`[usePractices] Loading practices for team: ${currentTeam.id}`);
      fetchPractices();
      }, 50);
      
      return () => clearTimeout(timeoutId);
    } else {
      console.log(`[usePractices] No team, clearing practices`);
      setPractices([]);
      setSelectedPractice(null);
    }
  }, [currentTeam?.id, fetchPractices]);

  // Handle practice deletion (caller is responsible for confirmation UI)
  const deletePractice = useCallback(async (practiceId: string) => {
    console.log(`[usePractices] Deleting practice ${practiceId}`);
    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from('practices')
        .delete()
        .eq('id', practiceId);

      if (error) throw error;

      // Clear selected practice if it was deleted
      if (selectedPractice?.id === practiceId) {
        setSelectedPractice(null);
      }
      
      // Refresh practices
      await fetchPractices();
      toast.success('Practice deleted successfully');
    } catch (error) {
      console.error('[usePractices] Error deleting practice:', error);
      toast.error('Failed to delete practice');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPractice, fetchPractices]);

  // Handle practice selection
  const selectPractice = useCallback((practice: Practice | null) => {
    console.log(`[usePractices] Selecting practice: ${practice?.id || 'none'}`);
    setSelectedPractice(practice);
  }, []);

  // Select newly created practice and close modal
  const selectNewPractice = useCallback((practice: Practice) => {
    console.log(`[usePractices] Selecting newly created practice: ${practice.id}`);
    setSelectedPractice(practice);
    setIsAddingPractice(false);
    setEditingPractice(null);
    // Refresh practices to show the new one in the list with proper sorting
    fetchPractices();
  }, [fetchPractices]);

  // Start editing a practice
  const startEditingPractice = useCallback((practice: Practice) => {
    console.log(`[usePractices] Start editing practice: ${practice.id}`);
    setEditingPractice(practice);
    setIsAddingPractice(true);
  }, []);

  // Start adding a new practice
  const startAddingPractice = useCallback(() => {
    console.log('[usePractices] Start adding new practice');
    setEditingPractice(null);
    setIsAddingPractice(true);
  }, []);

  // Handle practice form close
  const handlePracticeFormClose = useCallback(() => {
    console.log('[usePractices] Closing practice form');
    setIsAddingPractice(false);
    setEditingPractice(null);
  }, []);

  return {
    practices,
    selectedPractice,
    isLoading,
    isAddingPractice,
    editingPractice,
    fetchPractices,
    deletePractice,
    selectPractice,
    selectNewPractice,
    startEditingPractice,
    startAddingPractice,
    handlePracticeFormClose,
    setSelectedPractice,
    // Keep forceSelectPractice for backward compatibility
    forceSelectPractice: selectNewPractice
  };
} 